import _ from "lodash";
import React from "react";
import classNames from "classnames";
import {
  Tooltip,
  IconButton,
  Button,
  Modal,
  Checkbox,
} from "@toddle-edu/ds-web";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { MinusCircleOutlined } from "@toddle-edu/ds-icons";
import MessagingAvatar from "MessagingV2/components/ThreadList/Avatar/Avatar";

import useI18n from "Hooks/UseI18n";
import {
  isMessagingBlockingUserEnabled,
  isChannelSplitFeatureEnabled,
  SUB_TEXT_LENGTH,
} from "MessagingV2/modules/Utils";
import { BlockUserButton } from "MessagingV2/components/BlockUser";
import { StudentAlertsWrapper } from "AppComponents/StudentAlerts";
import useSharedLocalStorage from "Hooks/useSharedLocalStorage";

import classes from "./MemberCard.scss";

const REMOVE_MEMBER_LOCAL_STORAGE = "showMessagingV2RemoveMemberModal";

const RemoveUserModal = props => {
  const { name, onRemove, onClose } = props;
  const { t } = useI18n(["common"]);
  const [showModalAgain, setShowModalAgain] = useSharedLocalStorage(
    REMOVE_MEMBER_LOCAL_STORAGE,
    false
  );

  const toggleShowModalAgain = () => {
    setShowModalAgain(showModalAgain => !showModalAgain);
  };

  return (
    <Modal width="376px" height="max-content" onClose={onClose} dsVersion="2.0">
      <Modal.Header title={`${t("common:remove_user_from_channel")}?`} />
      <Modal.Body>
        <div className={classes.modalBody}>
          <div className="text-body">
            {t("common:remove_name_from_channel_info", {
              name,
            })}
          </div>
          <Checkbox
            dsVersion="2.0"
            onChange={toggleShowModalAgain}
            isChecked={showModalAgain}
          >
            <span className={classes.textSecondary}>
              {t("common:do_not_show_this_again")}
            </span>
          </Checkbox>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.modalFooter}>
          <Button
            dsVersion="2.0"
            variant="neutral"
            size="medium"
            type="outlined"
            onClick={onClose}
          >
            {t("common:keep")}
          </Button>
          <Button
            dsVersion="2.0"
            variant="primary"
            size="medium"
            type="fill"
            onClick={onRemove}
          >
            {t("common:remove")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const MemberCard = ({
  onRemoveClick,
  avatarId,
  mainText,
  subText,
  profileImage,
  isRemovable,
  isBlocked,
  userType,
  canBlockUser,
  alertTypes,
}) => {
  const { t } = useI18n(["common"]);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = React.useState(
    false
  );

  const [hideModal] = useSharedLocalStorage(REMOVE_MEMBER_LOCAL_STORAGE, false);

  const showBlockUserOption =
    canBlockUser && isMessagingBlockingUserEnabled(userType);

  const channelSplitFeatureEnabled = isChannelSplitFeatureEnabled();

  const onShowRemoveMemberModal = () => setShowRemoveMemberModal(true);
  const onCloseRemoveMemberModal = () => setShowRemoveMemberModal(false);

  const onClickRemoveButton = () => {
    if (hideModal) {
      onRemoveClick();
    } else {
      onShowRemoveMemberModal();
    }
  };

  return (
    <div
      className={classNames(classes.userTypeCardContainer, {
        [classes.userTypeCardWithPadding]: channelSplitFeatureEnabled,
      })}
    >
      <MessagingAvatar
        id={avatarId}
        name={mainText}
        url={profileImage}
        isBlocked={isBlocked}
        onlineStatus={false}
      />
      <div className={classes.userInfoContainer}>
        <StudentAlertsWrapper
          studentId={avatarId}
          alertTypes={alertTypes}
          className="text-body"
          hideAlerts={_.isEmpty(alertTypes)}
        >
          {mainText}
        </StudentAlertsWrapper>
        {subText ? (
          <ConditionalWrapper
            wrapper={<Tooltip tooltip={subText} />}
            condition={_.size(subText) > SUB_TEXT_LENGTH}
            type={"wrapperAsElement"}
          >
            <div className={classNames("text-body-s", classes.userSubTitle)}>
              {_.truncate(subText, { length: SUB_TEXT_LENGTH })}
            </div>
          </ConditionalWrapper>
        ) : null}
      </div>
      <div className={classes.iconButtonContainer}>
        {showBlockUserOption ? (
          <BlockUserButton
            userId={avatarId}
            isBlocked={isBlocked}
            name={mainText}
            userType={userType}
          />
        ) : null}
        {isRemovable ? (
          <Tooltip
            tooltip={t("common:remove_user_from_channel")}
            placement="bottom"
            showArrow
          >
            <IconButton
              dsVersion="2.0"
              icon={<MinusCircleOutlined />}
              type="plain"
              variant="neutral"
              size="medium"
              onClick={onClickRemoveButton}
              className={classes.removeIcon}
            />
          </Tooltip>
        ) : null}

        {showRemoveMemberModal ? (
          <RemoveUserModal
            name={mainText}
            onRemove={onRemoveClick}
            onClose={onCloseRemoveMemberModal}
          />
        ) : null}
      </div>
    </div>
  );
};

export default MemberCard;
